/**
 * HustleMeetCircleDetailPage.jsx — /circles/:slug
 *
 * Reads GET /api/hustlemeet-circles/:slug and lists upcoming events.
 * Booking links out to /hustle-events/:slug (existing checkout).
 * Iframes the vendor's menu (with ordering system if provided) so
 * members see exactly what they'll eat/order on the night.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { CalendarDays, MapPin, Users, ArrowRight, Ticket, Utensils,
         ExternalLink, User, Repeat, MessageSquare, Camera, Send,
         Trash2, UserPlus, LogOut, Vote, Trophy, Plus, X } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", day: "numeric", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function HustleMeetCircleDetailPage() {
  const { slugOrId } = useParams();
  const [data, setData] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [err,  setErr]  = useState("");
  // Sprint 2 — roster · chat · gallery
  const [members,  setMembers]  = useState({ host: null, members: [], total: 0 });
  const [messages, setMessages] = useState([]);
  const [photos,   setPhotos]   = useState([]);
  const [msgText,      setMsgText]      = useState("");
  const [photoUrl,     setPhotoUrl]     = useState("");
  const [photoCaption, setPhotoCaption] = useState("");
  const [meId,     setMeId]     = useState("");
  const [isMember, setIsMember] = useState(false);
  // Venue shortlist (host creates, members vote)
  const [shortlist, setShortlist] = useState(null);
  const [venueCap, setVenueCap]   = useState(3);
  const [myVote,   setMyVote]     = useState("");
  const [newOptions, setNewOptions] = useState([{ name: "", address: "", url: "" }]);
  const [showCreator, setShowCreator] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/hustlemeet-circles/${encodeURIComponent(slugOrId)}`);
        setData(data);
        // Best-effort vendor lookup for the menu iframe URL
        const vid = data?.circle?.vendor_id;
        if (vid) {
          try {
            const { data: vdata } = await axios.get(
              `${API}/vendors/${data.circle.slug}/public`);
            setVendor(vdata?.vendor || null);
          } catch { /* no-op */ }
        }
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message);
      }
    })();
  }, [slugOrId]);

  // Prefer explicit menu_url; fall back to vendor.website with a hash
  const menuUrl = useMemo(() => {
    const c = data?.circle || {};
    return c.menu_url || vendor?.menu_url || null;
  }, [data, vendor]);

  // Fetch Sprint 2 collections when circle loads
  useEffect(() => {
    const cid = data?.circle?.id;
    if (!cid) return;
    (async () => {
      let rosterData = null;
      try {
        const [rr, pp] = await Promise.all([
          axios.get(`${API}/hustlemeet-circles/${encodeURIComponent(cid)}/members`),
          axios.get(`${API}/hustlemeet-circles/${encodeURIComponent(cid)}/photos`),
        ]);
        rosterData = rr.data;
        setMembers(rr.data);
        setPhotos(pp.data?.photos || []);
      } catch { /* public endpoints — swallow */ }
      // Membership + first chat load — requires auth
      try {
        const { data: me } = await axios.get(`${API}/auth/me`);
        const myId = me?.id || me?.user?.id;
        if (myId) setMeId(myId);
        const iAmMember = data.circle.host_id === myId ||
          (rosterData?.members || []).some((m) => m.member_id === myId);
        setIsMember(iAmMember);
        if (iAmMember) {
          const { data: msgs } = await axios.get(
            `${API}/hustlemeet-circles/${encodeURIComponent(cid)}/messages`);
          setMessages(msgs.messages || []);
        }
      } catch { /* anon */ }
    })();
  }, [data]);

  // SSE chat stream (with 5s poll fallback if the CDN/network drops the stream)
  useEffect(() => {
    if (!isMember || !data?.circle?.id) return;
    const cid = data.circle.id;
    const url = `${API}/hustlemeet-circles/${encodeURIComponent(cid)}/messages/stream`;
    const token = (typeof window !== "undefined") ? localStorage.getItem("hu_session_token") : null;
    const controller = new AbortController();
    let pollT = null;
    (async () => {
      try {
        const resp = await fetch(url, {
          signal: controller.signal,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!resp.ok || !resp.body) throw new Error(`sse status ${resp.status}`);
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const frames = buf.split("\n\n");
          buf = frames.pop() || "";
          for (const frame of frames) {
            const line = frame.split("\n").find((l) => l.startsWith("data: "));
            if (!line) continue;
            try {
              const msg = JSON.parse(line.slice(6));
              setMessages((prev) => {
                if (prev.some((x) => x.id === msg.id)) return prev;
                return [...prev, msg].sort((a, b) =>
                  (a.created_at_iso || a.created_at || "")
                    .localeCompare(b.created_at_iso || b.created_at || ""));
              });
            } catch { /* ignore malformed frame */ }
          }
        }
      } catch {
        // Fallback to 5s polling if streaming is blocked
        pollT = setInterval(async () => {
          try {
            const { data: msgs } = await axios.get(
              `${API}/hustlemeet-circles/${encodeURIComponent(cid)}/messages`);
            setMessages(msgs.messages || []);
          } catch { /* ignore */ }
        }, 5000);
      }
    })();
    return () => {
      controller.abort();
      if (pollT) clearInterval(pollT);
    };
  }, [isMember, data?.circle?.id]);

  async function joinCircle() {
    if (!data?.circle?.id) return;
    try {
      await axios.post(`${API}/hustlemeet-circles/${data.circle.id}/members/join`);
      setIsMember(true);
      const { data: rr } = await axios.get(
        `${API}/hustlemeet-circles/${data.circle.id}/members`);
      setMembers(rr);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  }
  async function leaveCircle() {
    if (!data?.circle?.id) return;
    if (!window.confirm("Leave this Circle?")) return;
    try {
      await axios.post(`${API}/hustlemeet-circles/${data.circle.id}/members/leave`);
      setIsMember(false);
      setMessages([]);
      const { data: rr } = await axios.get(
        `${API}/hustlemeet-circles/${data.circle.id}/members`);
      setMembers(rr);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  }
  async function sendMessage() {
    const text = msgText.trim();
    if (!text || !data?.circle?.id) return;
    try {
      const { data: m } = await axios.post(
        `${API}/hustlemeet-circles/${data.circle.id}/messages`,
        { text });
      setMessages((prev) => [...prev, m]);
      setMsgText("");
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  }
  async function deleteMessage(msgId) {
    if (!window.confirm("Delete this message?")) return;
    try {
      await axios.delete(
        `${API}/hustlemeet-circles/${data.circle.id}/messages/${msgId}`);
      setMessages((prev) => prev.filter((m) => m.id !== msgId));
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  }
  async function addPhoto() {
    const url = photoUrl.trim();
    if (!url || !data?.circle?.id) return;
    try {
      const { data: p } = await axios.post(
        `${API}/hustlemeet-circles/${data.circle.id}/photos`,
        { url, caption: photoCaption });
      setPhotos((prev) => [p, ...prev]);
      setPhotoUrl(""); setPhotoCaption("");
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  }
  async function uploadPhotoFile(fileInput) {
    const file = fileInput?.files?.[0];
    if (!file || !data?.circle?.id) return;
    if (file.size > 10 * 1024 * 1024) {
      alert("Photo too large — 10 MB max"); return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", photoCaption);
    try {
      const { data: p } = await axios.post(
        `${API}/hustlemeet-circles/${data.circle.id}/photos/upload`,
        fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((prev) => [p, ...prev]);
      setPhotoCaption("");
      fileInput.value = "";
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  }
  async function deletePhoto(photoId) {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await axios.delete(
        `${API}/hustlemeet-circles/${data.circle.id}/photos/${photoId}`);
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  }

  // ── Venue shortlist voting ─────────────────────────────────────
  async function refreshShortlist() {
    if (!data?.circle?.id) return;
    try {
      const { data: r } = await axios.get(
        `${API}/hustlemeet-circles/${data.circle.id}/venue-shortlists/open`);
      setShortlist(r?.shortlist || null);
    } catch { setShortlist(null); }
  }
  useEffect(() => {
    if (!data?.circle?.id) return;
    (async () => {
      try {
        const { data: s } = await axios.get(`${API}/hustlemeet-settings`);
        const n = parseInt(s?.venue_shortlist_size, 10);
        if (Number.isFinite(n) && n >= 2 && n <= 8) setVenueCap(n);
      } catch { /* keep default */ }
      await refreshShortlist();
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.circle?.id]);

  async function createShortlist() {
    const valid = newOptions.filter((o) => o.name.trim());
    if (valid.length < 2) {
      alert("Add at least 2 venue options"); return;
    }
    try {
      const { data: r } = await axios.post(
        `${API}/hustlemeet-circles/${data.circle.id}/venue-shortlists`,
        { title: "Where should we eat?",
          options: valid.map((o) => ({
            name:    o.name.trim(),
            address: (o.address || "").trim(),
            url:     (o.url || "").trim(),
          })) });
      setShortlist(r);
      setShowCreator(false);
      setNewOptions([{ name: "", address: "", url: "" }]);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  }
  async function castVote(optionId) {
    if (!shortlist) return;
    try {
      const { data: r } = await axios.post(
        `${API}/hustlemeet-venue-shortlists/${shortlist.id}/vote`,
        { option_id: optionId });
      if (r?.shortlist) setShortlist(r.shortlist);
      setMyVote(optionId);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  }
  async function closeShortlist() {
    if (!shortlist) return;
    if (!window.confirm("Close voting and tally the winner?")) return;
    try {
      const { data: r } = await axios.post(
        `${API}/hustlemeet-venue-shortlists/${shortlist.id}/close`);
      if (r?.shortlist) setShortlist(r.shortlist);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  }
  async function cancelShortlist() {
    if (!shortlist) return;
    if (!window.confirm("Cancel this shortlist? No winner will be picked.")) return;
    try {
      await axios.post(
        `${API}/hustlemeet-venue-shortlists/${shortlist.id}/cancel`);
      setShortlist(null);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  }

  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0a0908] flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="circle-error">
          <p className="text-lg font-black">Circle not found</p>
          <p className="mt-2 text-stone-500 text-sm">{err}</p>
          <Link to="/meet" className="inline-block mt-6 text-amber-500 font-bold hover:underline">
            Back to HustleMeet →
          </Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div
        data-testid="circle-loading"
        className="min-h-screen bg-slate-50 dark:bg-[#0a0908] flex items-center justify-center text-stone-400"
      >Loading…</div>
    );
  }

  const c = data.circle;
  const events = data.upcoming_events || [];
  const pastVenues = data.past_venues || [];
  const crewSize = data.crew_size || 0;

  return (
    <div
      data-testid="hustlemeet-circle-detail"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: c.name, to: `/circles/${c.slug}` }}
        showBookCta={false} showCart={false}
      />

      {/* Hero */}
      <section
        data-testid="circle-hero"
        className="relative overflow-hidden border-b border-stone-200 dark:border-stone-800"
        style={c.cover_image ? {
          backgroundImage: `linear-gradient(135deg, rgba(10,9,8,.6), rgba(10,9,8,.75)), url(${c.cover_image})`,
          backgroundSize: "cover", backgroundPosition: "center",
        } : { background: "linear-gradient(135deg,#0a0908,#1c1917)" }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 text-white">
          <p className="text-amber-400 text-[11px] font-black uppercase tracking-[0.32em]">
            HustleMeet Circle
          </p>
          <h1 className="mt-2 text-3xl md:text-5xl font-black leading-tight" data-testid="circle-name">
            {c.name}
          </h1>
          {c.tagline && (
            <p className="mt-3 text-lg text-white/80 max-w-2xl">{c.tagline}</p>
          )}
          <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-white/80">
            {c.venue_address && (
              <span className="inline-flex items-center gap-1">
                <MapPin size={14}/> {c.venue_address}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <Users size={14}/> Hosted by {c.host_name}
            </span>
            <span className="inline-flex items-center gap-1">
              <CalendarDays size={14}/> {c.cadence}
              {c.time_hint ? ` · ${c.time_hint}` : ""}
            </span>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
        {c.description && (
          <section data-testid="circle-description">
            <h2 className="text-2xl font-black mb-4">About this Circle</h2>
            <p className="text-base leading-relaxed text-stone-600 dark:text-stone-300 whitespace-pre-line">
              {c.description}
            </p>
          </section>
        )}

        {/* Host card */}
        <section data-testid="circle-host-card"
                 className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6 flex items-start gap-5">
          {c.host_avatar_url ? (
            <img
              src={c.host_avatar_url}
              alt={c.host_name || "Host"}
              className="w-16 h-16 rounded-full object-cover shrink-0 ring-2 ring-amber-500/30"
            />
          ) : (
            <span
              className="inline-flex w-16 h-16 rounded-full items-center justify-center bg-amber-500/15 text-amber-500 shrink-0"
              title={c.host_name || "Host"}
              aria-hidden="true"
            >
              <User size={26} strokeWidth={1.75} />
            </span>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-black uppercase tracking-[0.28em] text-amber-500 dark:text-amber-400">
              Your host
            </p>
            <h3 className="mt-1 text-xl font-black text-stone-900 dark:text-stone-50" data-testid="circle-host-name">
              {c.host_name || "Circle host"}
            </h3>
            {c.host_bio && (
              <p className="mt-2 text-sm text-stone-600 dark:text-stone-300 leading-relaxed whitespace-pre-line">
                {c.host_bio}
              </p>
            )}
          </div>
        </section>

        {/* The crew */}
        <section data-testid="circle-crew"
                 className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6">
          <div className="flex items-center gap-3">
            <Users size={20} className="text-amber-500" strokeWidth={1.75} title="The crew" />
            <h2 className="text-xl font-black text-stone-900 dark:text-stone-50">The crew</h2>
          </div>
          <p className="mt-2 text-sm text-stone-600 dark:text-stone-300 leading-relaxed">
            {crewSize > 0
              ? <>Currently <strong className="text-stone-900 dark:text-stone-100">{crewSize}</strong> {crewSize === 1 ? "member" : "members"}
                  {c.member_cap ? <> of {c.member_cap} seats</> : null}.
                  {c.member_cap && crewSize < c.member_cap ? " Room for a few more." : null}
                </>
              : "A new crew — be one of the first to join."}
          </p>
        </section>

        {/* Where we've met — past venues rotation */}
        {pastVenues.length > 0 && (
          <section data-testid="circle-past-venues"
                   className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Repeat size={20} className="text-amber-500" strokeWidth={1.75} title="Where we've met" />
              <h2 className="text-xl font-black text-stone-900 dark:text-stone-50">Where we&apos;ve met</h2>
            </div>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2" data-testid="past-venues-list">
              {pastVenues.map((v, i) => (
                <li key={`${v.venue_name}-${i}`} data-testid={`past-venue-${i}`}
                    className="text-sm text-stone-700 dark:text-stone-300 flex items-start gap-2 py-1.5">
                  <MapPin size={13} className="text-stone-500 mt-1 shrink-0" aria-hidden="true" />
                  <span>
                    <span className="font-semibold text-stone-900 dark:text-stone-100">{v.venue_name}</span>
                    {v.venue_address && <span className="block text-xs text-stone-500">{v.venue_address}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {menuUrl && (
          <section data-testid="circle-menu-iframe">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-2xl font-black">
                <Utensils size={20} className="inline mr-2 text-amber-500 -mt-1"/>
                Menu & ordering
              </h2>
              <a
                href={menuUrl} target="_blank" rel="noreferrer"
                className="text-xs font-bold text-amber-500 inline-flex items-center gap-1 hover:underline"
                data-testid="link-open-menu"
              >
                Open in new tab <ExternalLink size={12}/>
              </a>
            </div>
            <div className="rounded-2xl overflow-hidden border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950">
              <iframe
                data-testid="menu-iframe"
                src={menuUrl}
                title="Vendor menu"
                loading="lazy"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                className="w-full"
                style={{ height: "720px", border: "0" }}
              />
            </div>
            <p className="mt-2 text-[11px] text-stone-500">
              Ordering drinks via the Hustle app earns you 5% Hustle Credit — the payer earns the credit on split orders.
            </p>
          </section>
        )}

        <section data-testid="circle-upcoming">
          <h2 className="text-2xl font-black mb-6">Upcoming dinners</h2>
          {events.length === 0 && (
            <div className="rounded-2xl border border-dashed border-stone-300 dark:border-stone-700 p-8 text-center">
              <Ticket size={28} className="mx-auto text-amber-500"/>
              <p className="mt-3 text-sm text-stone-500">
                No dinners scheduled yet. Follow this Circle to be notified when the host publishes the next date.
              </p>
            </div>
          )}
          {events.length > 0 && (
            <ul className="space-y-3">
              {events.map((ev) => (
                <li key={ev.id}
                  data-testid={`circle-event-${ev.id}`}
                  className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-5 flex flex-col sm:flex-row sm:items-center gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-black">{ev.title}</p>
                    <div className="mt-1 flex flex-wrap gap-3 text-xs text-stone-500">
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays size={12}/> {fmt(ev.starts_at)}
                      </span>
                      {ev.venue_name && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={12}/> {ev.venue_name}
                        </span>
                      )}
                      {ev.feast_select_mode && (
                        <span className="inline-flex items-center gap-1">
                          <Utensils size={12}/> {ev.feast_select_mode.replace(/_/g, " ")}
                        </span>
                      )}
                    </div>
                  </div>
                  <Link
                    to={`/hustle-events/${ev.slug || ev.id}`}
                    className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black px-5 py-2.5 text-sm inline-flex items-center gap-1"
                    data-testid={`circle-event-book-${ev.id}`}
                  >
                    Book seat <ArrowRight size={14}/>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Venue voting */}
        <section data-testid="circle-venue-vote"
                 className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <Vote size={20} className="text-amber-500" strokeWidth={1.75} />
              <h2 className="text-xl font-black text-stone-900 dark:text-stone-50">Where should we eat?</h2>
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500">
                Members vote · host picks the winner · up to {venueCap} options
              </span>
            </div>
            {meId === c.host_id && !shortlist && !showCreator && (
              <button data-testid="btn-open-shortlist-creator"
                      onClick={() => { setShowCreator(true); setNewOptions(Array.from({length: Math.min(3, venueCap)}).map(() => ({name:"",address:"",url:""}))); }}
                      className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black px-4 py-2 text-sm">
                <Plus size={14} /> Start a shortlist
              </button>
            )}
          </div>

          {/* Host creator */}
          {meId === c.host_id && showCreator && !shortlist && (
            <div className="space-y-3" data-testid="shortlist-creator">
              {newOptions.map((o, i) => (
                <div key={i} className="grid sm:grid-cols-3 gap-2 items-center">
                  <input value={o.name}
                         onChange={(e) => setNewOptions((prev) => prev.map((x,idx) => idx===i ? {...x, name:e.target.value} : x))}
                         placeholder={`Venue #${i+1} name`}
                         data-testid={`shortlist-opt-name-${i}`}
                         className="rounded-md border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 px-3 py-2 text-sm" />
                  <input value={o.address}
                         onChange={(e) => setNewOptions((prev) => prev.map((x,idx) => idx===i ? {...x, address:e.target.value} : x))}
                         placeholder="Address / suburb"
                         data-testid={`shortlist-opt-addr-${i}`}
                         className="rounded-md border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 px-3 py-2 text-sm" />
                  <div className="flex items-center gap-2">
                    <input value={o.url}
                           onChange={(e) => setNewOptions((prev) => prev.map((x,idx) => idx===i ? {...x, url:e.target.value} : x))}
                           placeholder="Website (optional)"
                           data-testid={`shortlist-opt-url-${i}`}
                           className="flex-1 rounded-md border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 px-3 py-2 text-sm" />
                    <button onClick={() => setNewOptions((prev) => prev.filter((_,idx) => idx!==i))}
                            title="Remove option"
                            data-testid={`btn-remove-opt-${i}`}
                            className="text-stone-400 hover:text-red-500">
                      <X size={14} />
                    </button>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-2 pt-2">
                <button data-testid="btn-add-opt"
                        onClick={() => setNewOptions((prev) => prev.length < venueCap ? [...prev, {name:"",address:"",url:""}] : prev)}
                        disabled={newOptions.length >= venueCap}
                        className="rounded-full border border-stone-300 dark:border-stone-700 hover:bg-stone-50 dark:hover:bg-stone-900 font-bold px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-40">
                  <Plus size={12} /> Add option ({newOptions.length}/{venueCap})
                </button>
                <button data-testid="btn-open-shortlist" onClick={createShortlist}
                        className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black px-4 py-1.5 text-xs">
                  Open voting
                </button>
                <button data-testid="btn-cancel-creator"
                        onClick={() => { setShowCreator(false); setNewOptions([{name:"",address:"",url:""}]); }}
                        className="text-xs text-stone-500 hover:text-stone-700">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Open shortlist: vote */}
          {shortlist && shortlist.status === "open" && (
            <div className="space-y-3" data-testid="shortlist-voting">
              <p className="text-sm text-stone-600 dark:text-stone-300">
                {shortlist.voters_total || 0} vote{(shortlist.voters_total || 0) === 1 ? "" : "s"} so far.
                {!isMember && meId !== c.host_id && " Join the Circle to vote."}
              </p>
              <ul className="space-y-2">
                {(shortlist.options || []).map((o) => {
                  const v = (shortlist.votes_by_option || {})[o.id] || 0;
                  const total = shortlist.voters_total || 0;
                  const pct = total ? Math.round(v * 100 / total) : 0;
                  const mine = myVote === o.id;
                  return (
                    <li key={o.id} data-testid={`shortlist-option-${o.id}`}
                        className={`rounded-xl border p-4 transition-colors ${mine ? "border-amber-400 bg-amber-50/40 dark:bg-amber-500/10" : "border-stone-200 dark:border-stone-800"}`}>
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-black text-stone-900 dark:text-stone-100">{o.name}</p>
                          {o.address && <p className="text-xs text-stone-500">{o.address}</p>}
                          {o.url && <a href={o.url} target="_blank" rel="noreferrer"
                                       className="text-xs text-amber-500 hover:underline inline-flex items-center gap-1 mt-0.5">
                            Visit <ExternalLink size={10} />
                          </a>}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-bold tabular-nums text-stone-500">{v} · {pct}%</span>
                          <button data-testid={`btn-vote-${o.id}`}
                                  onClick={() => castVote(o.id)}
                                  disabled={!(isMember || meId === c.host_id)}
                                  className={`rounded-full px-4 py-1.5 text-xs font-black transition-colors ${mine ? "bg-amber-500 text-slate-950" : "border border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-200 hover:border-amber-400"} disabled:opacity-40`}>
                            {mine ? "Your vote" : "Vote"}
                          </button>
                        </div>
                      </div>
                      <div className="mt-2 rounded-full bg-stone-100 dark:bg-stone-900 h-1.5 overflow-hidden">
                        <div className="h-full bg-amber-500 transition-all duration-500" style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
              {meId === c.host_id && (
                <div className="flex items-center gap-3 pt-2">
                  <button data-testid="btn-close-shortlist" onClick={closeShortlist}
                          className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black px-4 py-1.5 text-xs">
                    <Trophy size={12} className="inline-block mr-1" /> Close voting & pick winner
                  </button>
                  <button data-testid="btn-cancel-shortlist" onClick={cancelShortlist}
                          className="text-xs text-stone-500 hover:text-red-500">
                    Cancel shortlist
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Closed shortlist: winner banner */}
          {shortlist && shortlist.status === "closed" && (() => {
            const winner = (shortlist.options || []).find(o => o.id === shortlist.winner_option_id);
            return (
              <div className="rounded-xl border border-amber-300 dark:border-amber-500/40 bg-amber-50/60 dark:bg-amber-500/5 p-4" data-testid="shortlist-winner">
                <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                  <Trophy size={16} />
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em]">Winner</p>
                </div>
                <p className="mt-1 text-lg font-black text-stone-900 dark:text-stone-100">
                  {winner?.name || "No winner"}
                </p>
                {winner?.address && <p className="text-xs text-stone-500">{winner.address}</p>}
                <p className="mt-2 text-xs text-stone-500">
                  {shortlist.voters_total || 0} vote{(shortlist.voters_total || 0) === 1 ? "" : "s"} · closed {shortlist.closed_at?.slice(0,10)}
                </p>
                {meId === c.host_id && (
                  <button data-testid="btn-new-shortlist"
                          onClick={() => { setShortlist(null); setShowCreator(true); setNewOptions(Array.from({length: Math.min(3, venueCap)}).map(() => ({name:"",address:"",url:""}))); }}
                          className="mt-3 rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black px-4 py-1.5 text-xs">
                    Start a new shortlist
                  </button>
                )}
              </div>
            );
          })()}

          {!shortlist && !showCreator && (
            <p className="text-sm text-stone-500" data-testid="shortlist-empty">
              No open shortlist. {meId === c.host_id ? "Start one above." : "The host will open one before the next dinner."}
            </p>
          )}
        </section>

        {/* Sprint 2 — Members · Chat · Gallery */}
        {/* Members roster */}
        <section data-testid="circle-roster"
                 className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <Users size={20} className="text-amber-500" strokeWidth={1.75} />
              <h2 className="text-xl font-black text-stone-900 dark:text-stone-50">Members roster</h2>
            </div>
            {meId && !isMember && !c.is_invite_only && (
              <button data-testid="btn-join-circle" onClick={joinCircle}
                      className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black px-4 py-2 text-sm">
                <UserPlus size={14} /> Join Circle
              </button>
            )}
            {meId && isMember && meId !== c.host_id && (
              <button data-testid="btn-leave-circle" onClick={leaveCircle}
                      className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-200 hover:bg-stone-50 dark:hover:bg-stone-900 font-bold px-4 py-2 text-sm">
                <LogOut size={14} /> Leave
              </button>
            )}
          </div>
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="roster-list">
            {members.host && (
              <li key="host" data-testid="roster-item-host"
                  className="flex items-center gap-3 rounded-xl border border-amber-300/60 dark:border-amber-500/40 bg-amber-50/60 dark:bg-amber-500/5 p-3">
                {members.host.member_photo
                  ? <img src={members.host.member_photo} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                  : <span className="w-10 h-10 rounded-full bg-amber-500/20 text-amber-600 grid place-items-center shrink-0"><User size={18} /></span>}
                <div className="min-w-0">
                  <p className="text-sm font-black text-stone-900 dark:text-stone-100 truncate">{members.host.member_name}</p>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-600 dark:text-amber-400">Host</p>
                </div>
              </li>
            )}
            {(members.members || []).map((m) => {
              const canKick = meId && meId === c.host_id && m.member_id !== c.host_id;
              return (
                <li key={m.id} data-testid={`roster-item-${m.member_id}`}
                    className="flex items-center gap-3 rounded-xl border border-stone-200 dark:border-stone-800 p-3">
                  {m.member_photo
                    ? <img src={m.member_photo} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                    : <span className="w-10 h-10 rounded-full bg-stone-100 dark:bg-stone-900 text-stone-500 grid place-items-center shrink-0"><User size={18} /></span>}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-stone-900 dark:text-stone-100 truncate">{m.member_name}</p>
                    <p className="text-[10px] text-stone-500">Joined {fmt(m.joined_at)}</p>
                  </div>
                  {canKick && (
                    <button data-testid={`btn-kick-${m.member_id}`}
                            onClick={async () => {
                              if (!window.confirm(`Kick ${m.member_name}?`)) return;
                              try {
                                await axios.post(
                                  `${API}/hustlemeet-circles/${c.id}/members/${m.member_id}/kick`,
                                  { reason: "" });
                                setMembers((prev) => ({ ...prev,
                                  members: prev.members.filter((x) => x.member_id !== m.member_id) }));
                              } catch (e) { alert(e?.response?.data?.detail || e.message); }
                            }}
                            title="Kick member"
                            className="text-stone-400 hover:text-red-500">
                      <Trash2 size={14} />
                    </button>
                  )}
                </li>
              );
            })}
            {(!members.members || members.members.length === 0) && !members.host && (
              <li className="col-span-full text-sm text-stone-500">No members yet. Be the first to join.</li>
            )}
          </ul>
        </section>

        {/* Group chat */}
        <section data-testid="circle-chat"
                 className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6">
          <div className="flex items-center gap-3 mb-4">
            <MessageSquare size={20} className="text-amber-500" strokeWidth={1.75} />
            <h2 className="text-xl font-black text-stone-900 dark:text-stone-50">Circle chat</h2>
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500">Members only · updates every 5s</span>
          </div>
          {!isMember && (
            <p className="text-sm text-stone-500">Join the Circle to read and post in the chat.</p>
          )}
          {isMember && (
            <>
              <ul className="space-y-3 max-h-[420px] overflow-y-auto pr-2" data-testid="chat-list">
                {messages.length === 0 && (
                  <li className="text-sm text-stone-500">No messages yet. Say hi to the crew.</li>
                )}
                {messages.map((m) => (
                  <li key={m.id} data-testid={`chat-msg-${m.id}`} className="flex items-start gap-3">
                    {m.author_photo
                      ? <img src={m.author_photo} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                      : <span className="w-8 h-8 rounded-full bg-stone-100 dark:bg-stone-900 text-stone-500 grid place-items-center shrink-0"><User size={14} /></span>}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs">
                        <span className="font-black text-stone-900 dark:text-stone-100">{m.author_name}</span>
                        <span className="ml-2 text-stone-400">{fmt(m.created_at_iso || m.created_at)}</span>
                      </p>
                      <p className="mt-0.5 text-sm text-stone-800 dark:text-stone-100 whitespace-pre-wrap break-words">{m.text}</p>
                    </div>
                    {(m.author_id === meId || meId === c.host_id) && (
                      <button data-testid={`btn-delete-msg-${m.id}`}
                              onClick={() => deleteMessage(m.id)}
                              title="Delete message"
                              className="text-stone-400 hover:text-red-500 shrink-0">
                        <Trash2 size={13} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex items-center gap-2">
                <input data-testid="chat-input" value={msgText}
                       onChange={(e) => setMsgText(e.target.value)}
                       onKeyDown={(e) => { if (e.key === "Enter") sendMessage(); }}
                       placeholder="Say something to the crew…"
                       className="flex-1 rounded-full border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 text-sm text-stone-900 dark:text-stone-100 px-4 py-2 focus:outline-none focus:border-amber-500" />
                <button data-testid="btn-send-msg" onClick={sendMessage}
                        className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black px-4 py-2 text-sm inline-flex items-center gap-1">
                  <Send size={14} /> Send
                </button>
              </div>
            </>
          )}
        </section>

        {/* Sprint 2.1 · recurrence + discussion threads (per-circle) */}
        <RecurrencePanel eventId={c.id} isHost={!!(meId && meId === c.host_id)} />
        <ThreadsPanel eventId={c.id} meId={meId} />

        {/* Photo gallery */}
        <section data-testid="circle-gallery"
                 className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Camera size={20} className="text-amber-500" strokeWidth={1.75} />
            <h2 className="text-xl font-black text-stone-900 dark:text-stone-50">Photo gallery</h2>
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500">Members can add · anyone can view</span>
          </div>
          {isMember && (
            <div className="mb-5 space-y-2" data-testid="gallery-uploader">
              <div className="flex flex-col sm:flex-row gap-2">
                <label className="flex-1 rounded-full border border-dashed border-amber-400 bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 px-4 py-2 text-sm font-bold cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-500/20 inline-flex items-center gap-2"
                       data-testid="gallery-file-label">
                  <Camera size={14} /> Upload a photo (JPG / PNG / WebP · 10 MB)
                  <input type="file" accept="image/*"
                         data-testid="gallery-file-input"
                         onChange={(e) => uploadPhotoFile(e.target)}
                         className="sr-only" />
                </label>
                <input value={photoCaption}
                       onChange={(e) => setPhotoCaption(e.target.value)}
                       placeholder="Caption (optional) — applies to next upload or URL"
                       className="sm:w-72 rounded-full border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 text-sm text-stone-900 dark:text-stone-100 px-4 py-2 focus:outline-none focus:border-amber-500"
                       data-testid="gallery-caption-input" />
              </div>
              <details className="text-xs text-stone-500">
                <summary className="cursor-pointer">Or paste an image URL instead</summary>
                <div className="mt-2 flex flex-col sm:flex-row gap-2">
                  <input value={photoUrl}
                         onChange={(e) => setPhotoUrl(e.target.value)}
                         placeholder="https://…image.jpg"
                         className="flex-1 rounded-full border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 text-sm text-stone-900 dark:text-stone-100 px-4 py-2 focus:outline-none focus:border-amber-500"
                         data-testid="gallery-url-input" />
                  <button data-testid="btn-add-photo" onClick={addPhoto}
                          className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black px-4 py-2 text-sm inline-flex items-center gap-1">
                    Add URL
                  </button>
                </div>
              </details>
            </div>
          )}
          {photos.length === 0 && (
            <p className="text-sm text-stone-500">No photos yet.{isMember ? " Add the first one above." : ""}</p>
          )}
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="gallery-list">
            {photos.map((p) => {
              const canDelete = meId && (p.uploader_id === meId || meId === c.host_id);
              return (
                <li key={p.id} data-testid={`gallery-item-${p.id}`}
                    className="relative rounded-xl overflow-hidden border border-stone-200 dark:border-stone-800 group">
                  <img src={p.thumb_url || p.url} alt={p.caption || "Circle photo"}
                       loading="lazy"
                       className="w-full h-40 object-cover" />
                  {(p.caption || p.uploader_name) && (
                    <div className="p-2 text-[11px] text-stone-600 dark:text-stone-300 bg-white/90 dark:bg-slate-900/80">
                      {p.caption && <p className="line-clamp-2">{p.caption}</p>}
                      <p className="text-[10px] text-stone-400 mt-0.5">— {p.uploader_name}</p>
                    </div>
                  )}
                  {canDelete && (
                    <button data-testid={`btn-delete-photo-${p.id}`}
                            onClick={() => deletePhoto(p.id)}
                            title="Delete photo"
                            className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 size={12} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      </div>

      <SuiteFooter />
    </div>
  );
}


/* ─── Sprint 2.1: Recurring meetups editor ────────────────────────
 *   Hosts flip a one-off event into a weekly/fortnightly/monthly
 *   series. Weekday chips are toggleable; the API projects the next
 *   6 occurrences so members can see the cadence at a glance.
 */
const WEEKDAY_CHIPS = [
  { key: 0, label: "Mon" },
  { key: 1, label: "Tue" },
  { key: 2, label: "Wed" },
  { key: 3, label: "Thu" },
  { key: 4, label: "Fri" },
  { key: 5, label: "Sat" },
  { key: 6, label: "Sun" },
];

function RecurrencePanel({ eventId, isHost }) {
  const [cadence, setCadence] = useState("weekly");
  const [interval, setInt]    = useState(1);
  const [weekdays, setWDays]  = useState([]);
  const [dom, setDom]         = useState(15);
  const [until, setUntil]     = useState("");
  const [max, setMax]         = useState("");
  const [busy, setBusy]       = useState(false);
  const [preview, setPreview] = useState(null);
  const [err, setErr]         = useState("");

  useEffect(() => {
    axios.get(`${API}/hustlemeet/event/${eventId}/upcoming?n=6`).then(({ data }) => {
      setPreview(data);
    }).catch(() => {});
  }, [eventId]);

  const toggleDay = (k) => setWDays((prev) =>
    prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);

  const save = async () => {
    if (!isHost) return;
    setBusy(true); setErr("");
    try {
      const payload = { cadence, interval,
        weekdays, day_of_month: dom,
        until: until || null,
        max_occurrences: max ? Number(max) : null };
      const { data } = await axios.post(
        `${API}/hustlemeet/event/${eventId}/recurrence`, payload);
      setPreview({ series_id: data.series_id,
                    occurrences: data.upcoming.map((o) => ({ ...o, realised: false })) });
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!isHost || !window.confirm("Cancel this recurring series?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/hustlemeet/event/${eventId}/recurrence`);
      setPreview(null); setWDays([]);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid="event-recurrence"
             className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6">
      <div className="flex items-center gap-3 mb-4">
        <Repeat size={20} className="text-amber-500" strokeWidth={1.75} />
        <h2 className="text-xl font-black text-stone-900 dark:text-stone-50">Recurring meetup</h2>
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500">
          {isHost ? "Host edit · everyone views" : "View-only"}
        </span>
      </div>

      {isHost && (
        <>
          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            <label className="text-xs text-stone-600 dark:text-stone-300">
              Cadence
              <select value={cadence} onChange={(e) => setCadence(e.target.value)}
                      data-testid="rec-cadence"
                      className="mt-1 w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 text-sm px-3 py-2">
                <option value="weekly">Weekly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="monthly">Monthly</option>
              </select>
            </label>
            <label className="text-xs text-stone-600 dark:text-stone-300">
              Every N (1–8)
              <input type="number" min={1} max={8} value={interval}
                     onChange={(e) => setInt(Math.max(1, Math.min(8, +e.target.value || 1)))}
                     data-testid="rec-interval"
                     className="mt-1 w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 text-sm px-3 py-2" />
            </label>
            <label className="text-xs text-stone-600 dark:text-stone-300">
              Max occurrences (blank = unlimited)
              <input type="number" min={1} max={104} value={max}
                     onChange={(e) => setMax(e.target.value)}
                     data-testid="rec-max"
                     className="mt-1 w-full rounded-lg border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 text-sm px-3 py-2" />
            </label>
          </div>
          {cadence !== "monthly" && (
            <div className="mb-3">
              <div className="text-xs font-bold text-stone-600 dark:text-stone-300 mb-1">On these weekdays</div>
              <div className="flex flex-wrap gap-2" data-testid="rec-weekdays">
                {WEEKDAY_CHIPS.map((d) => (
                  <button key={d.key} type="button"
                          data-testid={`rec-wd-${d.label.toLowerCase()}`}
                          onClick={() => toggleDay(d.key)}
                          className={`px-3 py-1 rounded-full text-xs font-bold border transition ${
                            weekdays.includes(d.key)
                              ? "bg-amber-500 text-slate-950 border-amber-500"
                              : "bg-white dark:bg-slate-900 text-stone-600 dark:text-stone-300 border-stone-300 dark:border-stone-700"
                          }`}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {cadence === "monthly" && (
            <label className="block text-xs text-stone-600 dark:text-stone-300 mb-3">
              Day of month (1–31)
              <input type="number" min={1} max={31} value={dom}
                     onChange={(e) => setDom(Math.max(1, Math.min(31, +e.target.value || 15)))}
                     data-testid="rec-dom"
                     className="mt-1 w-40 rounded-lg border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 text-sm px-3 py-2" />
            </label>
          )}
          <label className="block text-xs text-stone-600 dark:text-stone-300 mb-3">
            End on (optional)
            <input type="date" value={until}
                   onChange={(e) => setUntil(e.target.value)}
                   data-testid="rec-until"
                   className="mt-1 w-56 rounded-lg border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 text-sm px-3 py-2" />
          </label>
          <div className="flex gap-2 mb-3">
            <button onClick={save} disabled={busy}
                    data-testid="rec-save"
                    className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black px-4 py-2 text-sm inline-flex items-center gap-1 disabled:opacity-50">
              <Repeat size={14} /> {busy ? "Saving…" : "Save series"}
            </button>
            {preview?.series_id && (
              <button onClick={clear} disabled={busy}
                      data-testid="rec-clear"
                      className="rounded-full bg-stone-200 dark:bg-slate-800 text-stone-700 dark:text-stone-200 hover:bg-stone-300 font-black px-4 py-2 text-sm inline-flex items-center gap-1 disabled:opacity-50">
                <X size={14} /> Cancel series
              </button>
            )}
          </div>
          {err && <p className="text-xs text-red-500 mb-3">{err}</p>}
        </>
      )}

      {preview && Array.isArray(preview.occurrences) && preview.occurrences.length > 0 ? (
        <div data-testid="rec-preview">
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-stone-500 mb-2">Next occurrences</div>
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {preview.occurrences.slice(0, 6).map((o, i) => (
              <li key={i} data-testid={`rec-occ-${i}`}
                  className={`rounded-lg border p-2 text-xs ${
                    o.realised
                      ? "bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-700/40"
                      : "bg-stone-50 border-stone-200 text-stone-700 dark:bg-slate-900 dark:border-stone-800 dark:text-stone-300"
                  }`}>
                <div className="font-bold">
                  {o.date || (o.starts_at || "").slice(0, 10)}
                </div>
                <div className="text-[10px] opacity-80">
                  {o.weekday || ""}
                  {o.realised ? " · scheduled" : " · projected"}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-sm text-stone-500">
          {isHost
            ? "Not recurring yet. Configure above to auto-generate future dates."
            : "This event doesn't repeat."}
        </p>
      )}
    </section>
  );
}


/* ─── Sprint 2.1: In-app chat threads per event ──────────────────
 *   Newest-first list of top-level posts with an expandable reply
 *   drawer. Anyone can read, anyone signed-in can post.
 */
function ThreadsPanel({ eventId, meId }) {
  const [threads, setThreads] = useState([]);
  const [openId, setOpenId]   = useState("");
  const [replies, setReplies] = useState({});
  const [text, setText]       = useState("");
  const [replyText, setReplyText] = useState("");
  const [name, setName]       = useState(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("hm_thread_name") || "";
  });
  const [busy, setBusy]       = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/hustlemeet/event/${eventId}/threads?limit=50`);
      setThreads(data.threads || []);
    } catch { /* public — swallow */ }
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [eventId]);

  const post = async () => {
    const body = text.trim();
    if (!body || !name.trim()) return;
    setBusy(true);
    try {
      localStorage.setItem("hm_thread_name", name.trim());
      await axios.post(`${API}/hustlemeet/event/${eventId}/threads`, {
        author_id: meId || `guest_${name.trim()}`,
        author_name: name.trim(),
        body,
      });
      setText(""); await load();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const openThread = async (tid) => {
    if (openId === tid) { setOpenId(""); return; }
    setOpenId(tid);
    try {
      const { data } = await axios.get(`${API}/hustlemeet/threads/${tid}`);
      setReplies((prev) => ({ ...prev, [tid]: data.replies || [] }));
    } catch { /* no-op */ }
  };

  const postReply = async (tid) => {
    const body = replyText.trim();
    if (!body || !name.trim()) return;
    try {
      await axios.post(`${API}/hustlemeet/threads/${tid}/reply`, {
        author_id: meId || `guest_${name.trim()}`,
        author_name: name.trim(),
        body,
      });
      setReplyText("");
      const { data } = await axios.get(`${API}/hustlemeet/threads/${tid}`);
      setReplies((prev) => ({ ...prev, [tid]: data.replies || [] }));
      setThreads((prev) => prev.map((t) => t.thread_id === tid
        ? { ...t, reply_count: (t.reply_count || 0) + 1 }
        : t));
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  const del = async (tid) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`${API}/hustlemeet/threads/${tid}`);
      await load();
      if (openId === tid) setOpenId("");
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  return (
    <section data-testid="event-threads"
             className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6">
      <div className="flex items-center gap-3 mb-4">
        <MessageSquare size={20} className="text-amber-500" strokeWidth={1.75} />
        <h2 className="text-xl font-black text-stone-900 dark:text-stone-50">Discussion</h2>
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500">
          {threads.length} thread{threads.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="mb-4 grid sm:grid-cols-[160px_1fr_auto] gap-2" data-testid="thread-composer">
        <input value={name} onChange={(e) => setName(e.target.value)}
               placeholder="Your name"
               data-testid="thread-name"
               className="rounded-full border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 text-sm px-4 py-2 focus:outline-none focus:border-amber-500" />
        <input value={text} onChange={(e) => setText(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); post(); } }}
               placeholder="Start a new thread…"
               data-testid="thread-body"
               className="rounded-full border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 text-sm px-4 py-2 focus:outline-none focus:border-amber-500" />
        <button onClick={post} disabled={busy || !text.trim() || !name.trim()}
                data-testid="thread-post"
                className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 disabled:opacity-40 font-black px-4 py-2 text-sm inline-flex items-center gap-1">
          <Send size={14} /> Post
        </button>
      </div>

      {threads.length === 0 && (
        <p className="text-sm text-stone-500">No threads yet. Kick it off above.</p>
      )}

      <ul className="space-y-3" data-testid="thread-list">
        {threads.map((t) => {
          const isMine = t.author_id === meId
            || (typeof window !== "undefined" && t.author_id === `guest_${localStorage.getItem("hm_thread_name") || ""}`);
          const isOpen = openId === t.thread_id;
          return (
            <li key={t.thread_id}
                data-testid={`thread-${t.thread_id}`}
                className="rounded-xl border border-stone-200 dark:border-stone-800 p-3">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs">
                    <span className="font-black text-stone-900 dark:text-stone-100">{t.author_name}</span>
                    <span className="ml-2 text-stone-400">{fmt(t.created_at)}</span>
                  </p>
                  <p className="mt-0.5 text-sm text-stone-800 dark:text-stone-100 whitespace-pre-wrap break-words">
                    {t.body}
                  </p>
                  <button onClick={() => openThread(t.thread_id)}
                          data-testid={`thread-toggle-${t.thread_id}`}
                          className="mt-2 text-[11px] font-bold uppercase tracking-[0.14em] text-amber-600 dark:text-amber-400 hover:underline inline-flex items-center gap-1">
                    <MessageSquare size={11} />
                    {isOpen ? "Hide replies" : `${t.reply_count || 0} repl${(t.reply_count || 0) === 1 ? "y" : "ies"}`}
                  </button>
                </div>
                {isMine && (
                  <button onClick={() => del(t.thread_id)}
                          data-testid={`thread-del-${t.thread_id}`}
                          title="Delete post"
                          className="text-stone-400 hover:text-red-500 shrink-0">
                    <Trash2 size={13} />
                  </button>
                )}
              </div>

              {isOpen && (
                <div className="mt-3 ml-3 pl-3 border-l-2 border-stone-200 dark:border-stone-800 space-y-2"
                     data-testid={`thread-replies-${t.thread_id}`}>
                  {(replies[t.thread_id] || []).map((rp) => (
                    <div key={rp.thread_id} data-testid={`reply-${rp.thread_id}`}>
                      <p className="text-[11px]">
                        <span className="font-black text-stone-900 dark:text-stone-100">{rp.author_name}</span>
                        <span className="ml-2 text-stone-400">{fmt(rp.created_at)}</span>
                      </p>
                      <p className="mt-0.5 text-xs text-stone-800 dark:text-stone-100 whitespace-pre-wrap break-words">
                        {rp.body}
                      </p>
                    </div>
                  ))}
                  <div className="mt-2 flex items-center gap-2">
                    <input value={replyText}
                           onChange={(e) => setReplyText(e.target.value)}
                           onKeyDown={(e) => { if (e.key === "Enter") postReply(t.thread_id); }}
                           placeholder="Reply…"
                           data-testid={`reply-input-${t.thread_id}`}
                           className="flex-1 rounded-full border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-slate-900 text-xs px-3 py-1.5 focus:outline-none focus:border-amber-500" />
                    <button onClick={() => postReply(t.thread_id)}
                            disabled={!replyText.trim() || !name.trim()}
                            data-testid={`reply-send-${t.thread_id}`}
                            className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 disabled:opacity-40 font-black px-3 py-1 text-xs">
                      Reply
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
